import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Check, Lock, Users, Repeat, ArrowRight } from "lucide-react";
import { useDiscoveryChecklist } from "@core/hooks/useDiscoveryChecklist";
import { UsageAnalyticsTracker } from "@core/services/UsageAnalyticsTracker";
import type { DiscoveryStepId } from "@core/lib/discoveryChecklist";

const ICONS: Record<DiscoveryStepId, React.ElementType> = {
  group: Users,
  recurring: Repeat,
};

/** Où mène l'étape quand le plan la permet. */
const ROUTES: Record<DiscoveryStepId, string> = {
  group: "/groupes",
  recurring: "/date",
};

/**
 * « Ce que vous n'avez pas encore essayé » — carte de découverte du tableau
 * de bord (P0).
 *
 * Elle ne s'affiche que pour les sondages de dates : les groupes réutilisables
 * et les séries récurrentes n'existent que là. Elle disparaît d'elle-même dès
 * que tout est fait, et ne s'affiche pas tant que les décomptes ne sont pas
 * connus (cf. `deriveDiscoverySteps`).
 *
 * Une étape verrouillée reste affichée et mène aux tarifs : c'est exactement
 * ce qu'on cherche à faire découvrir à un compte Gratuit. Le verrou réel est
 * côté serveur (trigger `date_poll_groups_enforce_plan`), la carte ne fait
 * que refléter.
 */
export const DiscoveryChecklist: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const steps = useDiscoveryChecklist();
  const tracked = useRef(false);

  useEffect(() => {
    if (steps && !tracked.current) {
      const hasLocked = steps.some((s) => s.locked && !s.done);
      if (hasLocked) {
        tracked.current = true;
        void UsageAnalyticsTracker.track("feature_gate_seen", {
          location: "discovery_checklist",
        });
      }
    }
  }, [steps]);

  if (!steps) return null;

  return (
    <section
      className="mb-6 rounded-xl border border-border bg-card p-5"
      data-testid="discovery-checklist"
      aria-labelledby="discovery-checklist-title"
    >
      <h2
        id="discovery-checklist-title"
        className="text-sm font-semibold text-foreground"
      >
        {t("discovery.title")}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("discovery.subtitle")}
      </p>

      <ul className="mt-4 space-y-2">
        {steps.map((step) => {
          const Icon = ICONS[step.id];
          return (
            <li key={step.id}>
              <button
                type="button"
                onClick={() =>
                  // Déjà fait l'emporte sur verrouillé : un compte Gratuit qui
                  // a gardé un groupe d'avant le verrou doit pouvoir aller le
                  // gérer, pas se faire renvoyer aux tarifs pour ce qu'il a.
                  navigate(
                    step.locked && !step.done ? "/pricing" : ROUTES[step.id],
                  )
                }
                className="flex w-full items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-left transition-colors hover:bg-muted/60"
                data-testid={`discovery-step-${step.id}`}
              >
                <span
                  className={
                    step.done
                      ? "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-success/20 text-success"
                      : "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
                  }
                  aria-hidden="true"
                >
                  {step.done ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Icon className="h-4 w-4" />
                  )}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-foreground">
                    {t(`discovery.step_${step.id}.title`)}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {t(`discovery.step_${step.id}.description`)}
                  </span>
                </span>

                {step.locked && !step.done ? (
                  <span className="flex flex-shrink-0 items-center gap-1 text-xs text-muted-foreground">
                    <Lock className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">
                      {t("discovery.locked_label")}
                    </span>
                  </span>
                ) : (
                  <ArrowRight
                    className="h-4 w-4 flex-shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
